import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Alert } from 'react-native';
import { useDispatch } from 'react-redux';
import type { AppDispatch } from '../../store';
import {
  addActiveNotification,
  setNotificationPermission,
} from '../../store/slices/notificationIntelligenceSlice';
import { IntelligentNotificationCenter } from './IntelligentNotificationCenter';
import { useNotificationEngagement } from '../../hooks/useNotificationEngagement';
import {
  mockSmartNotifications,
  generateMockNotification,
} from './notificationMockData';
import { logger } from '../../utils/logger';
import type {
  SmartNotification,
  AINotificationType,
} from '@coffeeplexity/shared';

interface NotificationCenterExampleProps {
  userId: string;
}

export const NotificationCenterExample: React.FC<
  NotificationCenterExampleProps
> = ({ userId }) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showNotificationCenter, setShowNotificationCenter] = useState(false);

  const {
    recordEngagement,
    submitFeedback,
    getEngagementMetrics,
    getTimingRecommendations,
    analyzeUserPatterns,
    triggerOptimization,
    isAnalyzing,
    isOptimizing,
  } = useNotificationEngagement(userId);

  // Initialize with mock data
  useEffect(() => {
    // Set notification permission
    dispatch(setNotificationPermission('granted'));

    // Add some mock notifications
    mockSmartNotifications.forEach(notification => {
      dispatch(addActiveNotification(notification));
    });
  }, [dispatch]);

  // Handle notification press
  const handleNotificationPress = async (notification: SmartNotification) => {
    try {
      await recordEngagement(notification, 'clicked');

      Alert.alert(
        'Notification Clicked',
        `You clicked: ${notification.title}\n\nThis would navigate to the relevant screen.`,
        [
          {
            text: 'Provide Feedback',
            onPress: () => handleFeedbackPress(notification.id),
          },
          { text: 'OK', style: 'default' },
        ]
      );
    } catch (error) {
      logger.error('Failed to handle notification press:', error);
    }
  };

  // Handle feedback
  const handleFeedbackPress = (notificationId: string) => {
    Alert.alert('Notification Feedback', 'How was this notification?', [
      {
        text: 'Helpful',
        onPress: () =>
          submitFeedback(notificationId, {
            feedbackType: 'helpful',
            rating: 5,
            context: { source: 'example_demo' },
          }),
      },
      {
        text: 'Not Helpful',
        onPress: () =>
          submitFeedback(notificationId, {
            feedbackType: 'not_helpful',
            rating: 2,
            context: { source: 'example_demo' },
          }),
      },
      {
        text: 'Wrong Time',
        onPress: () =>
          submitFeedback(notificationId, {
            feedbackType: 'wrong_time',
            rating: 2,
            context: { source: 'example_demo' },
          }),
      },
      { text: 'Cancel', style: 'cancel' },
    ]);
  };

  // Add a new mock notification
  const addMockNotification = (type: AINotificationType) => {
    const newNotification = generateMockNotification(type);
    dispatch(addActiveNotification(newNotification));
  };

  // Show engagement metrics
  const showMetrics = () => {
    const metrics = getEngagementMetrics();
    const recommendations = getTimingRecommendations();

    Alert.alert(
      'Engagement Metrics',
      `Total Notifications: ${metrics.totalNotifications}\n` +
        `Engagement Rate: ${Math.round(metrics.engagementRate * 100)}%\n` +
        `Click-through Rate: ${Math.round(metrics.clickThroughRate * 100)}%\n` +
        `Dismissal Rate: ${Math.round(metrics.dismissalRate * 100)}%\n\n` +
        `AI Recommendations:\n${recommendations.recommendations.join('\n')}\n\n` +
        `Confidence: ${Math.round(recommendations.confidence * 100)}%`
    );
  };

  if (showNotificationCenter) {
    return (
      <View style={styles.fullScreen}>
        <IntelligentNotificationCenter
          userId={userId}
          onNotificationPress={handleNotificationPress}
          onSettingsPress={() => {
            Alert.alert('Settings', 'This would open notification settings', [
              {
                text: 'Close',
                onPress: () => setShowNotificationCenter(false),
              },
              { text: 'OK' },
            ]);
          }}
        />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <Text style={styles.title}>Intelligent Notification Center Demo</Text>
      <Text style={styles.description}>
        This demo shows the AI-powered notification center with smart timing,
        personalization, and engagement tracking.
      </Text>

      <View style={styles.buttonContainer}>
        <TouchableOpacity
          style={styles.primaryButton}
          onPress={() => setShowNotificationCenter(true)}
        >
          <Text style={styles.primaryButtonText}>Open Notification Center</Text>
        </TouchableOpacity>

        <TouchableOpacity style={styles.secondaryButton} onPress={showMetrics}>
          <Text style={styles.secondaryButtonText}>
            Show Engagement Metrics
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.secondaryButton, isAnalyzing && styles.disabledButton]}
          onPress={analyzeUserPatterns}
          disabled={isAnalyzing}
        >
          <Text style={styles.secondaryButtonText}>
            {isAnalyzing ? 'Analyzing...' : 'Analyze Patterns'}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[
            styles.secondaryButton,
            isOptimizing && styles.disabledButton,
          ]}
          onPress={triggerOptimization}
          disabled={isOptimizing}
        >
          <Text style={styles.secondaryButtonText}>
            {isOptimizing ? 'Optimizing...' : 'Optimize Timing'}
          </Text>
        </TouchableOpacity>
      </View>

      <View style={styles.mockSection}>
        <Text style={styles.sectionTitle}>Add Mock Notifications:</Text>
        <View style={styles.mockButtonContainer}>
          <TouchableOpacity
            style={styles.mockButton}
            onPress={() =>
              addMockNotification(AINotificationType.MILESTONE_ACHIEVEMENT)
            }
          >
            <Text style={styles.mockButtonText}>🏆 Achievement</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.mockButton}
            onPress={() =>
              addMockNotification(AINotificationType.STREAK_REMINDER)
            }
          >
            <Text style={styles.mockButtonText}>🔥 Streak</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.mockButton}
            onPress={() =>
              addMockNotification(AINotificationType.NEW_RECOMMENDATION)
            }
          >
            <Text style={styles.mockButtonText}>💡 Recipe</Text>
          </TouchableOpacity>

          <TouchableOpacity
            style={styles.mockButton}
            onPress={() =>
              addMockNotification(AINotificationType.CONTENT_SUGGESTION)
            }
          >
            <Text style={styles.mockButtonText}>📚 Content</Text>
          </TouchableOpacity>
        </View>
      </View>

      <View style={styles.infoSection}>
        <Text style={styles.infoTitle}>Features Demonstrated:</Text>
        <Text style={styles.infoText}>• AI-optimized notification timing</Text>
        <Text style={styles.infoText}>
          • Personalized content recommendations
        </Text>
        <Text style={styles.infoText}>• Engagement pattern analysis</Text>
        <Text style={styles.infoText}>• User feedback collection</Text>
        <Text style={styles.infoText}>• Automatic preference learning</Text>
        <Text style={styles.infoText}>• Confidence indicators</Text>
        <Text style={styles.infoText}>• AI reasoning explanations</Text>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    backgroundColor: '#FFFFFF',
  },
  fullScreen: {
    flex: 1,
  },
  title: {
    fontSize: 24,
    fontWeight: '700',
    color: '#2C3E50',
    marginBottom: 12,
    textAlign: 'center',
  },
  description: {
    fontSize: 16,
    color: '#7F8C8D',
    lineHeight: 24,
    marginBottom: 24,
    textAlign: 'center',
  },
  buttonContainer: {
    marginBottom: 32,
  },
  primaryButton: {
    backgroundColor: '#3498DB',
    paddingVertical: 16,
    paddingHorizontal: 24,
    borderRadius: 12,
    alignItems: 'center',
    marginBottom: 12,
  },
  primaryButtonText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  secondaryButton: {
    backgroundColor: '#F8F9FA',
    paddingVertical: 12,
    paddingHorizontal: 20,
    borderRadius: 8,
    alignItems: 'center',
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E9ECEF',
  },
  secondaryButtonText: {
    fontSize: 16,
    fontWeight: '500',
    color: '#495057',
  },
  disabledButton: {
    opacity: 0.6,
  },
  mockSection: {
    marginBottom: 32,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 12,
  },
  mockButtonContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  mockButton: {
    backgroundColor: '#E8F5E8',
    paddingVertical: 8,
    paddingHorizontal: 12,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: '#27AE60',
  },
  mockButtonText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#27AE60',
  },
  infoSection: {
    backgroundColor: '#F8F9FA',
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E9ECEF',
  },
  infoTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#2C3E50',
    marginBottom: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#6C757D',
    lineHeight: 20,
    marginBottom: 4,
  },
});
